import { toApiError } from "../../api/api-error";
import { readJson } from "../../api/auth-api";
/**
 * `GET` a path and decode the body.
 *
 * VALIDATED, not cast: `readJson` runs the schema and throws `ApiError`
 * (`error.internal`) on a shape the contract does not describe, so a changed
 * server arrives as one error at the boundary instead of `undefined` deep in a
 * screen. A non-2xx throws the server's own `ApiError` — key, params and
 * `requestId` — unchanged.
 */
export async function getJson(api, path, schema, init) {
    const response = await api.authorizedFetch(path, {
        method: "GET",
        ...(init?.signal === undefined ? {} : { signal: init.signal }),
    });
    return await readJson(response, schema);
}
/**
 * Send a JSON body and decode the JSON answer.
 *
 * `body` is serialized here, once, to a string — the replayable shape the 401
 * retry needs. `undefined` sends no body at all (a `POST` whose contract is an
 * empty input), never the string `"undefined"`.
 */
export async function sendJson(api, method, path, body, schema) {
    const response = await api.authorizedFetch(path, writeInit(method, body));
    return await readJson(response, schema);
}
/**
 * Send a request whose success has NO JSON body — a `204`, or the `201`/`200`
 * with an empty body `POST /me/exclusions` answers.
 *
 * `readJson` would throw `error.internal` on an empty body, which is why the
 * deletes and the body-less writes come here. The body of a 2xx is never read,
 * so a server that later adds one breaks nothing; a non-2xx still throws the
 * server's `ApiError`.
 */
export async function sendNoContent(api, method, path, body) {
    const response = await api.authorizedFetch(path, writeInit(method, body));
    if (!response.ok) {
        throw await toApiError(response);
    }
}
function writeInit(method, body) {
    if (body === undefined) {
        return { method };
    }
    return {
        body: JSON.stringify(body),
        headers: { "content-type": "application/json" },
        method,
    };
}
/**
 * Append a query string to a contract path, omitting `undefined` values and
 * encoding both halves — the same rule `toExerciseListQueryString` follows, for
 * the routes whose contract ships no builder of its own (`?draft&locale`,
 * `?cursor&limit&locale`, `?before&limit`). It builds QUERY STRINGS only: the
 * path always comes from a contract constant or builder.
 */
export function withQuery(path, params) {
    const pairs = Object.entries(params)
        .filter((entry) => entry[1] !== undefined)
        .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
    return pairs.length === 0 ? path : `${path}?${pairs.join("&")}`;
}
//# sourceMappingURL=request.js.map